import { extent } from 'd3-array';
import { scaleTime } from 'd3-scale';
import { pointer } from 'd3-selection';
import { timeDay, timeHour, timeMinute, timeMonth, timeSecond, timeWeek, timeYear } from 'd3-time';
import { timeFormatLocale } from 'd3-time-format';
import { scale, xDomainCount } from './d3Core';
import moment from 'moment';

import React, { Component } from 'react';
import { connect } from 'react-redux';

import { getI18n } from '../../../../AppReducer';
import Chart, { expandDomain } from './charts/Chart';
import LineChart from './charts/LineChart';
import CommonProps from './CommonProps';

import styles from './LineTooltip.css';
import Tooltip from './utils/Tooltip';

const TIME_FORMAT_LOCALE_EN = timeFormatLocale({
  dateTime: '%a %b %d %Y %H:%M:%S %Z',
  date: '%m/%d/%Y',
  time: '%H:%M:%S',
  periods: ['AM', 'PM'],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
});

const TIME_FORMAT_LOCALE_ES = timeFormatLocale({
  dateTime: '%a %d de %b de %Y %H:%M:%S %Z',
  date: '%d/%m/%Y',
  time: '%H:%M:%S',
  periods: ['AM', 'PM'],
  days: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
  shortDays: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  months: [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ],
  shortMonths: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
});

const defYDomains = {
  nps: [-100, 100],
  ces: [0, 100],
  csat: [0, 100],
  star_five: [1, 5],
};

class LineTooltip extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.onMouseMove = this.onMouseMove.bind(this);
  }

  onMouseMove(event) {
    const { data, x, y, chartSeries, margins } = this.props;
    const { xScaleSet, yScaleSet } = this;
    const svg = this.rootRef;
    if (!svg) {
      return;
    }
    const mouseX = pointer(event, svg)[0];
    const closest = data.reduce(
      (best, value, i) => {
        const pointX = xScaleSet(x(value)) + margins.left;
        const absx = Math.abs(pointX - mouseX);
        if (absx < best.value) {
          return { index: i, value: absx };
        }

        return best;
      },
      { index: -1, value: Number.MAX_SAFE_INTEGER },
    );

    if (this.state.lastIndex !== closest.index) {
      const xTooltip = xScaleSet(x(data[closest.index])) + margins.left;
      const yTooltip = yScaleSet(y(data[closest.index][chartSeries[0].field]));
      this.setState({
        xTooltip,
        yTooltip,
        contentTooltip: {
          point: data[closest.index],
        },
      });
      this.setState({ lastIndex: closest.index });
    }
  }

  onMouseLeave() {
    this.setState({
      xTooltip: 0,
      yTooltip: 0,
      contentTooltip: null,
      lastIndex: -1,
    });
  }

  render() {
    if (this.props.data !== null) {
      const { format } = this.props.locale === 'es' ? TIME_FORMAT_LOCALE_ES : TIME_FORMAT_LOCALE_EN;
      const formatMillisecond = format('.%L');
      const formatSecond = format(':%S');
      const formatMinute = format('%I:%M');
      const formatHour = format('%I %p');
      const formatDay = format('%a %d');
      const formatWeek = format('%b %d');
      const formatMonth = format('%b');
      const formatYear = format('%Y');

      const tickFormat = date => {
        return (
          timeSecond(date) < date
            ? formatMillisecond
            : timeMinute(date) < date
              ? formatSecond
              : timeHour(date) < date
                ? formatMinute
                : timeDay(date) < date
                  ? formatHour
                  : timeMonth(date) < date
                    ? timeWeek(date) < date
                      ? formatDay
                      : formatWeek
                    : timeYear(date) < date
                      ? formatMonth
                      : formatYear
        )(date);
      };

      let xDomain = xDomainCount(this.props, this.props.stack, this.props.horizontal);

      const nTicks = Math.round(this.props.width / 125);
      const xTickValues = scaleTime().domain(xDomain).ticks(nTicks);
      xDomain = expandDomain(xDomain, (1000 / this.props.width) * 0.03);

      const yDom = (data, chartSeries, y) => {
        const domainArr = chartSeries.map(d => {
          const { field } = d;
          const ext = extent(data, dt => {
            return y(dt[field]);
          });
          return ext;
        });
        const extentArr = extent([].concat(...domainArr));
        return extentArr;
      };

      let yDomain = yDom(this.props.data, this.props.chartSeries, this.props.y);

      if (yDomain[0] === yDomain[1] && defYDomains[this.props.surveyType]) {
        yDomain = defYDomains[this.props.surveyType];
      }

      yDomain = expandDomain(yDomain);

      if (xDomain[0] && xDomain[0].getTime && xDomain[0].getTime() === xDomain[1].getTime()) {
        xDomain[0] = moment(xDomain[0]).subtract(15, 'days').toDate();
        xDomain[1] = moment(xDomain[1]).add(15, 'days').toDate();
      }

      const newXScale = {
        scale: this.props.xScale,
        range: this.props.xRange || [
          0,
          this.props.width - this.props.margins.left - this.props.margins.right,
        ],
        domain: xDomain,
        bandPaddingInner: this.props.xBandPaddingInner,
        bandPaddingOuter: this.props.xBandPaddingOuter,
      };
      this.xScaleSet = scale(newXScale);

      const newYScale = {
        scale: this.props.yScale,
        range: this.props.yRange || [
          this.props.height - this.props.margins.top - this.props.margins.bottom,
          0,
        ],
        domain: yDomain,
        bandPaddingInner: this.props.yBandPaddingInner,
        bandPaddingOuter: this.props.yBandPaddingOuter,
      };

      this.yScaleSet = scale(newYScale);

      return (
        <div className={styles.root} onMouseLeave={this.onMouseLeave.bind(this)}>
          <Tooltip {...this.props} {...this.state}>
            {this.props.children}
          </Tooltip>
          <Chart
            {...this.props}
            {...this.state}
            yTicks={[5]}
            xTickFormat={tickFormat}
            yDomain={yDomain}
            xDomain={xDomain}
            onMouseMove={this.onMouseMove}
            svgRef={ref => {
              this.rootRef = ref;
            }}
          >
            <LineChart
              {...this.props}
              {...this.state}
              xAxisClassName={styles.axis}
              yAxisClassName={styles.axis}
              gridAxisClassName={styles.grid}
              xGridStyleClassName={styles.gridLine}
              yGridStyleClassName={styles.gridLine}
              gridStyleClassName={styles.gridLine}
              xTickPadding={15}
              xTickValues={xTickValues}
              yTickPadding={15}
              yGridCount={[5]}
              xAxisStyling={{
                ticksClassName: styles.axisTicks,
                pathClassName: styles.axisPath,
                textClassName: styles.axisText,
              }}
              yAxisStyling={{
                ticksClassName: styles.axisTicks,
                pathClassName: styles.axisPath,
                textClassName: styles.axisText,
              }}
              legendClassName={styles.legend}
              dotClassName={styles.dot}
              lineClassName={styles.line}
            />
          </Chart>
        </div>
      );
    }
    return null;
  }
}

const mapStateToProps = state => ({
  locale: getI18n(state).locale,
});

LineTooltip.defaultProps = { ...CommonProps };

export default connect(mapStateToProps)(LineTooltip);
